import * as React from "react";
import styled from "react-emotion";
import {Control, Field, Icon, TextArea} from "bloomer";
import Card from "../../../elements/Card";
import Button from "../../../elements/Button";
import {getNERResult} from "../../../api/models/NamedEntityRecognition";
import Entities from "../../../components/model/NamedEntityRecognition/Entities";
import getWordsCount from "../../../utils/WordsCount";
import SendGAEvent, {GAEventActions, GAEventModels} from "../../../utils/SendGAEvent";
import WordsLabel from "../../../elements/WordsLabel";
import ErrorUtils from "../../../utils/ErrorUtils";
import ModelContentContainer from "../../../elements/ModelContentContainer";
import SectionName from "../../../elements/SectionName";
import Spacer from "../../../elements/Spacer";
import {toast} from "react-toastify";
import {INVALID_FIELD_STYLE} from "../../../utils/Constants";

const InformationCard = styled("div")`
  border: 1px solid lightgrey;
  border-radius: 5px;
  padding: 10px;
  font-size: 14px;
  min-height: 30px;
  word-wrap: anywhere;
`;

const OuterSpan = styled("span")<{ bgColor: string, textColor: string }>`
  line-height: 36px;
  padding: 8px;
  border-radius: 4px;
  background-color: ${props => props.bgColor};
  color: ${props => props.textColor};
  cursor: default;
  white-space: nowrap;
`;

const InnerSpan = styled("span")<{ typeBgColor: string, typeTextColor: string }>`
  padding: 4px;
  border-radius: 2px;
  background-color: ${props => props.typeBgColor};
  color: ${props => props.typeTextColor};
  font-size: 10px;
  font-weight: bold;
  margin-left: 5px;
  vertical-align: middle;
  white-space: nowrap;
`;

const NormalSpan = styled("span")`
  line-height: 36px;
`;

const InputWarning = styled("span")`
  text-align: left;
  line-height: 30px;
  vertical-align: middle;
  font-size: 14px;
  color: red;
`;

interface INERModelState {
    loading: boolean;
    text: string;
    textInputEmpty: boolean;
    wordsCount: number;
    showLimitWarning: boolean;
    result: any;
    errorMessage: string;
}

class NERModel extends React.Component<{}, INERModelState> {
    public WORDS_LIMIT = 1000;

    constructor(props: {}) {
        super(props);

        this.state = {
            loading: false,
            text: "",
            textInputEmpty: false,
            wordsCount: 0,
            showLimitWarning: false,
            result: [],
            errorMessage: ""
        };
    }

    public componentDidMount() {
        SendGAEvent(GAEventActions.VIEW_MODEL, GAEventModels.NAMED_ENTITY_RECOGNITION);
    }

    public updateWordsCount = async () => {
        const wordsCount = getWordsCount(this.state.text);
        this.setState({
            wordsCount,
            showLimitWarning: wordsCount > this.WORDS_LIMIT
        });
    };

    public handleInputChange = (e: any) => {
        this.setState({
            text: e.target.value,
            textInputEmpty: false,
            errorMessage: ""
        }, () => {
            this.updateWordsCount();
        });
    };

    public onSubmit = async (e: any) => {
        e.target.blur();

        if (!this.state.text.trim() || this.state.loading || this.state.showLimitWarning) {

            this.setState({result: []});

            if (!this.state.text.trim()) {
                this.setState({
                    textInputEmpty: true,
                    showLimitWarning: false
                });
            }
            return;
        }

        this.setState({loading: true, result: [], errorMessage: ""});

        let errorOccurred = false;

        const response = await getNERResult(this.state.text.trim()).catch((error) => {
            if (ErrorUtils.requestLimitExceeded(error.response)) {
                this.setState({
                    loading: false
                });
                toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
            } else {
                this.setState({
                    loading: false,
                    errorMessage: "Unable to extract named entities."
                });
            }
            errorOccurred = true;
        });

        if (errorOccurred) {
            return;
        }

        let result: any = [];
        const text = this.state.text.trim();

        if (response && response.data && response.data.entities) {
            let lastIndex = 0;
            const entities = response.data.entities;
            entities.forEach((entity: any) => {
                const word = text.substring(entity.start_pos, entity.end_pos);
                const leftPart = text.substring(lastIndex, entity.start_pos);
                lastIndex = entity.end_pos;
                result.push({
                    type: 'text',
                    text: leftPart
                }, {
                    type: 'entity',
                    text: word,
                    tag: entity.tag,
                    config: Entities[entity.tag]
                });
            });

            result.push({
                type: 'text',
                text: text.substring(lastIndex, text.length)
            });

            result = result.filter((item: any) => {
                return item.text;
            });

            this.setState({
                loading: false,
                result
            });
        } else {
            this.setState({
                loading: false,
                errorMessage: "Unable to extract named entities."
            });
        }

        SendGAEvent(GAEventActions.CALL_MODEL, GAEventModels.NAMED_ENTITY_RECOGNITION);
    };

    public resetState = async (e: any) => {
        e.target.blur();

        this.setState({
            loading: false,
            text: "",
            textInputEmpty: false,
            wordsCount: 0,
            showLimitWarning: false,
            result: [],
            errorMessage: ""
        });
    };

    public render() {
        return (
            <ModelContentContainer>
                <Card>
                    <Field>
                        <Control>
                            <SectionName>Text</SectionName>
                            <TextArea
                                style={(this.state.textInputEmpty || this.state.showLimitWarning) ? INVALID_FIELD_STYLE : undefined}
                                type="text"
                                placeholder="Enter your text here"
                                value={this.state.text}
                                onChange={this.handleInputChange}
                                onPaste={this.handleInputChange}
                            />
                            <div>
                                {
                                    this.state.textInputEmpty ?
                                        <InputWarning>Please input a text.</InputWarning> : <></>
                                }
                                {
                                    this.state.showLimitWarning ? <InputWarning>
                                        Input size limit has been exceeded. Please decrease the size.
                                    </InputWarning> : <></>
                                }
                                <WordsLabel>{this.state.wordsCount}&nbsp;{this.state.wordsCount > 1 ? 'words' : 'word'}</WordsLabel>
                            </div>
                        </Control>
                    </Field>
                    <div>
                        <Button onClick={this.onSubmit}>Submit</Button>
                        <Button onClick={this.resetState} style={{marginLeft: "5px"}}>Reset</Button>
                    </div>
                    {
                        this.state.loading ? <div>
                            <Spacer/>
                            <span>Loading...</span>
                            <Icon className="fas fa-spinner fa-spin"/>
                        </div> : <></>
                    }
                    {
                        this.state.result.length && !this.state.loading ? <div>
                            <Spacer/>
                            <InformationCard>
                                {
                                    this.state.result.map((item: any) => {
                                        if (item.type === 'entity') {
                                            const config = item.config;
                                            return <span title={config.name}>
                            <OuterSpan
                                bgColor={config.bgColor}
                                textColor={config.textColor}
                            >{item.text}<InnerSpan
                                typeBgColor={config.typeBgColor}
                                typeTextColor={config.typeTextColor}
                            >{item.tag}</InnerSpan>
                          </OuterSpan>&nbsp;</span>;
                                        } else {
                                            return <NormalSpan>{item.text}&nbsp;</NormalSpan>;
                                        }
                                    })
                                }
                            </InformationCard>
                        </div> : <></>
                    }
                    {
                        this.state.errorMessage && !this.state.loading ? <div>
                            <Spacer/>
                            <InformationCard>{this.state.errorMessage}</InformationCard>
                        </div> : <></>
                    }
                </Card>
            </ModelContentContainer>
        );
    }
}

export default NERModel;


// WEBPACK FOOTER //
// ./src/routes/Dashboard/NamedEntityRecognition/NERModel.tsx